// Single source of truth for the sidebar, breadcrumbs, and page titles.
//
// v1.1: the sidebar is hand-curated (Finance > AP Purchase Invoice and
// Admin > Users/Audit). Automation entries are NOT auto-listed here; new
// automations get added explicitly when they ship. Routes that are
// mounted in the router but absent from the sidebar (e.g. /admin/audit,
// the legacy /automations/:slug/:id detail deep-link) are not represented
// in this file. The router lives in `src/App.tsx`.

export interface AppRoute {
  path: string;
  label: string;
  icon?: string;
  department?: string;
  hidden?: boolean;
}

// Kept for backward-compat with anything that still imports `routes`.
// The new sidebar reads `SIDEBAR_NAV` instead.
export const routes: AppRoute[] = [
  { path: '/admin/users', label: 'Users', icon: 'Users', department: 'Admin' },
  { path: '/admin/audit', label: 'Audit', icon: 'ClipboardList', department: 'Admin' },
  { path: '/login', label: 'Login', hidden: true },
];

// Sidebar tree, lifted from the polished ERP shell pattern. v1.1 ships
// with exactly the leaves we own. New automations append a leaf under
// their department when they go live; we do NOT add empty placeholder
// groups (AR / Tax / Audit / Procurement) until they have automations.
export interface SidebarLeaf {
  id: string;
  label: string;
  to: string;
}

export interface SidebarGroup {
  id: string;
  label: string;
  leaves: SidebarLeaf[];
}

export const SIDEBAR_NAV: SidebarGroup[] = [
  {
    id: 'finance',
    label: 'Finance',
    leaves: [
      {
        id: 'ap-purchase-invoice',
        label: 'AP Purchase Invoice',
        to: '/automations/ap-purchase-invoice',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    leaves: [
      { id: 'admin-users', label: 'Users', to: '/admin/users' },
      { id: 'admin-audit', label: 'Audit', to: '/admin/audit' },
    ],
  },
];

// Breadcrumb map consumed by AppShell + W1-C's <Breadcrumb segments={...} />.
// Keys are exact pathname matches. Pages with dynamic segments (e.g.
// /automations/:slug/:id) fall back to a derived crumb computed in
// AppShell — see resolveBreadcrumb below.
export interface BreadcrumbSegment {
  label: string;
  to?: string;
}

export const ROUTE_BREADCRUMBS: Record<string, BreadcrumbSegment[]> = {
  '/': [{ label: 'Dashboard' }],
  '/automations/ap-purchase-invoice': [
    { label: 'Dashboard', to: '/' },
    { label: 'Finance' },
    { label: 'AP Purchase Invoice' },
  ],
  '/admin/users': [
    { label: 'Dashboard', to: '/' },
    { label: 'Admin' },
    { label: 'Users' },
  ],
  '/admin/audit': [
    { label: 'Dashboard', to: '/' },
    { label: 'Admin' },
    { label: 'Audit' },
  ],
};

export function resolveBreadcrumb(pathname: string): BreadcrumbSegment[] {
  const exact = ROUTE_BREADCRUMBS[pathname];
  if (exact) return exact;

  // /automations/:slug/:id — deep-link to a specific request. Treat as a
  // sub-page of the AP Purchase Invoice queue for now (the only automation
  // shipped in v1.1). When a second automation lands, switch on the slug.
  if (pathname.startsWith('/automations/ap-purchase-invoice/')) {
    return [
      { label: 'Dashboard', to: '/' },
      { label: 'Finance' },
      { label: 'AP Purchase Invoice', to: '/automations/ap-purchase-invoice' },
      { label: 'Detail' },
    ];
  }

  return [];
}
